import { applyPricingAndCommission } from "./VdpPricingEngine"

const normalizeOwnershipSource = value => {
	const s = typeof value === "string" ? value.trim().toLowerCase() : ""
	if (s === "system" || s === "user" || s === "external") return s
	return "system"
}

const round2 = n => Math.round(n * 100) / 100
const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0)
const hasExplicitValue = value => value !== undefined && value !== null && String(value).trim() !== ""
const normalizeActiveBound = value => {
	const parsed = num(value)
	return parsed > 0 ? parsed : undefined
}
const hasActivePriceRange = ({ min, max }) => normalizeActiveBound(min) !== undefined || normalizeActiveBound(max) !== undefined

export const vdpNormalizer = raw => {
	const src = raw && typeof raw === "object" ? raw : {}
	const vdp = { ...src }

	vdp.vdpvisita = String(vdp.vdpvisita ?? "").trim()
	vdp.vdpinsumo = String(vdp.vdpinsumo ?? "").trim()
	vdp.vdprecip = String(vdp.vdprecip ?? "").trim()
	vdp.vdpdesc = String(vdp.vdpdesc ?? "").trim()
	vdp.vdpunmed = String(vdp.vdpunmed ?? "").trim()
	vdp.vdpobserv = String(vdp.vdpobserv ?? "").trim()

	vdp.vdpident = num(vdp.vdpident)
	vdp.vdpqtde = Math.max(0, num(vdp.vdpqtde ?? 1))
	vdp.vdpvlunit = num(vdp.vdpvlunit)
	vdp.vdpcomis = num(vdp.vdpcomis)
	vdp.vdpdescon = num(vdp.vdpdescon)
	vdp.vdpdesconp = num(vdp.vdpdesconp)

	vdp.in1preco = num(vdp.in1preco ?? vdp.vdpvlunit)
	vdp.in1comis = num(vdp.in1comis ?? vdp.vdpcomis)
	vdp.in9capac = num(vdp.in9capac)

	if (hasExplicitValue(src.in1precmin) && num(src.in1precmin) < 0) {
		throw new Error("Preço mínimo configurado inválido.")
	}

	if (hasExplicitValue(src.in1precmax) && num(src.in1precmax) < 0) {
		throw new Error("Preço máximo configurado inválido.")
	}

	if (hasActivePriceRange({ min: src.in1precmin, max: src.in1precmax })) {
		vdp.in1precmin = normalizeActiveBound(src.in1precmin)
		vdp.in1precmax = normalizeActiveBound(src.in1precmax)
	} else {
		vdp.in1precmin = undefined
		vdp.in1precmax = undefined
	}

	vdp._childrenVersion = num(vdp._childrenVersion)

	vdp.vdpncm = vdp.vdpncm ?? null
	vdp.vdppededi = vdp.vdppededi ?? null

	vdp.priceSource = normalizeOwnershipSource(src.priceSource)
	vdp.commissionSource = normalizeOwnershipSource(src.commissionSource)

	const ident = vdp.vdpident === 1 ? 1 : 0
	const bucket = ident === 1 ? "returnedItems" : "soldItems"

	const insumo = vdp.vdpinsumo
	const recip = vdp.vdprecip

	vdp.bucket = bucket
	vdp.localKey = `${insumo}_${recip}`
	vdp.parentKey = `${ident}:${vdp.localKey}`

	const variation = (insumo ? 1 : 0) + (recip ? 2 : 0)
	vdp.vdpIntentEnumValue = variation + 3 * ident

	if (!recip) {
		vdp.vdpqtdecil = 0
	} else {
		const cap = vdp.in9capac > 0 ? vdp.in9capac : 1
		if (!insumo) vdp.vdpqtdecil = vdp.vdpqtde
		else vdp.vdpqtdecil = vdp.vdpqtde > 0 ? Math.ceil(vdp.vdpqtde / cap) : 0
	}

	const bruto = round2(vdp.vdpqtde * vdp.vdpvlunit)

	let descon = Math.max(0, vdp.vdpdescon)
	let desconp = Math.max(0, vdp.vdpdesconp)

	if (desconp > 0) {
		descon = round2(bruto * (desconp / 100))
	} else if (descon > 0) {
		desconp = bruto > 0 ? round2((descon * 100) / bruto) : 0
	}

	descon = Math.min(descon, bruto)

	const total = round2(Math.max(0, bruto - descon))
	const comisValue = round2(total * (vdp.vdpcomis / 100))

	vdp.vdpvltotalRaw = bruto
	vdp.vdpvltotal = total
	vdp.vdpdescon = descon
	vdp.vdpdesconp = desconp
	vdp.vdpcomisValue = comisValue

	return vdp
}

export const projectVdpItem = (raw, pricingCtx) => {
	const base = vdpNormalizer(raw)
	const tmp = { ...base }
	applyPricingAndCommission(pricingCtx, tmp)
	return vdpNormalizer(tmp)
}

export async function createVdpItems({
	key,
	in1insumo,
	in1recip,
	amount = 1,
	related = false,
	priceRangeActive = false,
	fetchIn1ByCode,
	fetchCylinderFullInfo,
	vdpNormalizer,
}) {
	const ident = key === "soldItems" ? 0 : 1
	const sold = in1insumo ? await fetchIn1ByCode(in1insumo) : null

	let recip = null
	if (in1recip) {
		recip = (await fetchCylinderFullInfo?.(in1recip)) || null
		if (!recip) recip = await fetchIn1ByCode(in1recip)
	}

	const makeRaw = ({ soldItem = null, recipItem = null, qty = 1 }) => ({
		vdpvisita: "",
		vdpident: ident,

		vdpinsumo: soldItem?.in1insumo || "",
		vdpdesc: soldItem?.in1nome || "",
		vdpunmed: soldItem?.in1unmed || "",
		vdpqtde: qty ?? 1,
		vdpvlunit: soldItem?.in1preco ?? 0,

		vdprecip: recipItem?.in1insumo || "",
		vdpredesc: recipItem?.in1nome || "",
		in9capac: recipItem?.in9capac ?? 0,

		in1preco: soldItem?.in1preco ?? 0,
		in1comis: soldItem?.in1comis ?? 0,

		...(priceRangeActive &&
			hasActivePriceRange({
				min: soldItem?.in1precmin,
				max: soldItem?.in1precmax,
			}) && {
				in1precmin: soldItem?.in1precmin ?? undefined,
				in1precmax: soldItem?.in1precmax ?? undefined,
			}),

		vdpdescon: 0,
		vdpdesconp: 0,
		vdpcomis: 0,

		priceSource: "system",
		commissionSource: "system",

		children: [],
	})

	const rawItems = []

	if (related) {
		if (sold) rawItems.push(makeRaw({ soldItem: sold, recipItem: recip, qty: amount }))
	} else {
		if (sold) rawItems.push(makeRaw({ soldItem: sold, qty: amount }))
		if (recip) rawItems.push(makeRaw({ recipItem: recip, qty: 1 }))
	}

	return rawItems.map(vdpNormalizer)
}

export const vdpUpdater = (before, changes = {}) => {
	const hasOwn = (obj, k) => Object.prototype.hasOwnProperty.call(obj, k)

	const next = { ...before }

	let touchedUnit = false
	let touchedComis = false

	for (const [k, v] of Object.entries(changes || {})) {
		if (k === "vdpqtdecil") {
			const recip = String(next.vdprecip ?? "").trim()
			if (!recip) continue

			const cyl = Math.max(0, num(v))
			const capRaw = num(next.in9capac)
			const cap = capRaw > 0 ? capRaw : 1

			const insumo = String(next.vdpinsumo ?? "").trim()
			next.vdpqtde = insumo ? cyl * cap : cyl
			continue
		}

		if (k === "vdpvlunit") {
			touchedUnit = true
			const newPrice = num(v)
			const min = next.in1precmin
			const max = next.in1precmax

			if (newPrice < 0) throw new Error("Preço não pode ser negativo.")

			if (min !== undefined && newPrice < min)
				throw new Error(`Preço abaixo do mínimo permitido. Mínimo: ${min.toFixed(2)}`)

			if (max !== undefined && newPrice > max)
				throw new Error(`Preço acima do máximo permitido. Máximo: ${max.toFixed(2)}`)
		}

		if (k === "vdpcomis") touchedComis = true

		if (k === "vdpdescon") {
			const bruto = round2(next.vdpqtde * next.vdpvlunit)
			const abs = Math.max(0, num(v))

			next.vdpdescon = abs
			next.vdpdesconp = bruto > 0 ? (abs * 100) / bruto : 0

			continue
		}

		if (k === "vdpdesconp") {
			const bruto = round2(next.vdpqtde * next.vdpvlunit)
			const percent = Math.max(0, num(v))

			next.vdpdesconp = percent
			next.vdpdescon = bruto > 0 ? round2((bruto * percent) / 100) : 0
			continue
		}

		next[k] = v
	}

	const identityChanged = hasOwn(changes, "vdpinsumo") || hasOwn(changes, "vdprecip") || hasOwn(changes, "vdpident")

	const explicitPriceSource = hasOwn(changes, "priceSource")
	const explicitCommissionSource = hasOwn(changes, "commissionSource")

	if (touchedUnit && !explicitPriceSource) next.priceSource = "user"
	if (touchedComis && !explicitCommissionSource) next.commissionSource = "user"

	if (identityChanged) {
		if (!explicitPriceSource && !touchedUnit) next.priceSource = "system"
		if (!explicitCommissionSource && !touchedComis) next.commissionSource = "system"
	}

	if (explicitPriceSource) next.priceSource = normalizeOwnershipSource(next.priceSource)
	if (explicitCommissionSource) next.commissionSource = normalizeOwnershipSource(next.commissionSource)

	return vdpNormalizer(next)
}

export const unpinVdpPrice = vdp => {
	const base = vdpNormalizer(vdp)
	return vdpUpdater(base, { priceSource: "system" })
}

export const unpinVdpCommission = vdp => {
	const base = vdpNormalizer(vdp)
	return vdpUpdater(base, { commissionSource: "system" })
}
